/**
 * The one place that turns a value into KQL text.
 *
 * @remarks
 * No picker block ever puts a value next to a quote character itself. A block
 * hands over a value and an operator; what makes text out of it is not theirs.
 * That is what keeps the escaping provable on one spot instead of per
 * block, where one forgotten block is only a matter of time.
 *
 * @packageDocumentation
 */

const SINGLE_QUOTE = String.fromCodePoint(39);
const BACKSLASH = String.fromCodePoint(92);

/**
 * A single-quoted KQL string literal that cannot break out of its delimiters.
 *
 * @remarks
 * The escape is a BACKSLASH, not a doubled quote. KQL's ordinary single-quoted
 * literal honours backslash escapes; doubling belongs to the VERBATIM literal
 * (`@'...'`), which this function does not produce. Microsoft Learn, "The string
 * data type", accessed 2026-09-17
 * (https://learn.microsoft.com/en-us/kusto/query/scalar-data-types/string):
 * "Use the backslash character to escape the enclosing quote characters, tab
 * characters, newline characters, and the backslash itself."
 *
 * Getting this wrong is silent rather than loud. In an ordinary literal
 * `'o''brien'` is read as two adjacent literals that KQL concatenates, so the
 * query asks for `obrien`: no syntax error, no injection, just a different
 * question than the filler typed.
 *
 * This is KQL and only KQL. OData `$filter`, which the Entra pickers build,
 * is the other way round: there a quote IS escaped by doubling and a backslash
 * is not an escape character at all. Copying this function to that package
 * would make exactly the mistake it was written to fix, and just as silently.
 *
 * A backslash is doubled for the same reason a quote is escaped: a trailing one
 * would otherwise escape the closing quote and leave the literal open. Control
 * characters (C0 and DEL) are dropped entirely, because they have no place in a
 * resource name and could smuggle a line break into the query text.
 *
 * @param value - The raw value, from a template author, a referenced field or a
 * filler's search term. All three get the same treatment.
 * @returns A balanced `'...'` literal, so the value is inert data and never new
 * KQL.
 */
export function kqlStringLiteral(value: string): string {
  let escaped = '';
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) {
      continue;
    }
    if (char === BACKSLASH) {
      escaped += `${BACKSLASH}${BACKSLASH}`;
    } else if (char === SINGLE_QUOTE) {
      escaped += `${BACKSLASH}${SINGLE_QUOTE}`;
    } else {
      escaped += char;
    }
  }
  return `${SINGLE_QUOTE}${escaped}${SINGLE_QUOTE}`;
}

/**
 * The same literal for a list of values, as the `(…)` group an `in`-style
 * operator takes.
 */
export function kqlStringLiteralList(values: readonly string[]): string {
  return `(${values.map(kqlStringLiteral).join(', ')})`;
}
